import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useOutletContext } from 'react-router-dom';
import { authAPI, api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Users, Building2, FileText, Mail, Calendar, LogOut, Menu, X, Shield, Zap, FileSpreadsheet, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import AIAssistant from '@/components/AIAssistant';

const MainLayout = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [version, setVersion] = useState(null);

  useEffect(() => {
    api.get('/version').then((r) => setVersion(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const baseNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/leads', label: 'Leads', icon: Users },
    { path: '/lenders', label: 'Lenders', icon: Building2 },
    { path: '/term-sheets', label: 'Term Sheets', icon: FileText },
    { path: '/pricing', label: 'Pricing', icon: Calculator },
    { path: '/applications', label: 'Applications', icon: FileText },
    { path: '/followups', label: 'Follow-ups', icon: Calendar },
    { path: '/communications', label: 'Communications', icon: Mail },
    { path: '/email-parser', label: 'Email Parser', icon: Zap },
    { path: '/sheets-sync', label: 'Sheets Sync', icon: FileSpreadsheet },
  ];
  
  // Add Admin link for admin users
  const navItems = user?.role === 'admin' 
    ? [...baseNavItems, { path: '/admin', label: 'Admin', icon: Shield }]
    : baseNavItems;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-black/80 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_loan-tracker-75/artifacts/5da0ujdt_axe%20logo%20corregido-01.png" 
                alt="Axe Capital Lending" 
                className="h-10 w-auto"
              />
              <h1 className="text-xl font-bold text-primary hidden xl:block whitespace-nowrap" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Axe Capital Lending
              </h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1 flex-wrap justify-end">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu">
                    <Avatar>
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border">
            <nav className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                      }`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        <Outlet context={{ user }} />
      </main>

      <footer className="max-w-7xl mx-auto px-4 py-4 text-xs text-zinc-400 flex justify-end" data-testid="app-version-footer">
        {version ? `${version.environment} · build ${version.commit}` : ''}
      </footer>

      {/* AI Assistant */}
      <AIAssistant />
    </div>
  );
};

export default MainLayout;
